import EventManager from "../../../store/sqftEventManager";
import config from "../../../store/sqftConfig";
import SqftStore from "../../../store/sqftStore";
import workspace from "../../workspace";
import store from "../../../../../../../store";
import {updateSQFTCalculations} from "../../../../../../../actions/sqftFunctions";
import {getPointField} from "../../../../../helpers/sqftHelper";
import {getFreeFormAngleOutline} from "../../tools/freeeDraw/helpers/freeFormAngleOutline";
import {getFreeFormTurn, getFreeFormRayLimit, isSimpleFreeFormOutline, matchFreeFormOutline, rotateFreeFormContinuation} from "../../tools/freeeDraw/helpers/freeFormAngleHelper";
import NumberText from '../../workspace/text/numberText';
import COLOR_THEMES from '../../../store/colorThemes';
import {openFreeFormAnglePopup} from './freeFormAnglePopup';
import {recoverFreeFormCenterline} from '../../tools/freeeDraw/helpers/recoverFreeFormCenterline';

export default class ShapeFreeFormAngles {
  constructor(shape) {
    this.shape = shape;
    this.activeIndex = null;
    this.drag = null;
    this.previewPoints = null;
    this.hoveredIndex = null;
    this.angleLabels = [];
    this.hoveredSide = null;
  }

  registerEvents() {
    EventManager.removeEvent(this);
    EventManager.addEvent(EventManager.events.MOUSE_DOWN, this.onMouseDown, this, EventManager.priorities.OVERLAY);
    EventManager.addEvent(EventManager.events.MOUSE_MOVE, this.onMouseMove, this, EventManager.priorities.OVERLAY);
    EventManager.addEvent(EventManager.events.MOUSE_UP, this.onMouseUp, this, EventManager.priorities.OVERLAY);
    EventManager.addEvent(EventManager.events.KEYDOWN, this.onKeyDown, this, EventManager.priorities.OVERLAY);
    EventManager.addEvent(EventManager.events.MOUSE_LEAVE, this.onMouseLeave, this, EventManager.priorities.OVERLAY);
    EventManager.addEvent(EventManager.events.CONTEXTMENU, this.onContextMenu, this, EventManager.priorities.OVERALL - 1);
  }

  destroy() {
    EventManager.removeEvent(this);
    this.close();
    this.angleLabels.forEach(label => label.destroy());
    this.angleLabels = [];
  }

  close() {
    const angleInput = this.angleInput;
    this.angleInput = null;
    if (angleInput) {
      angleInput.close();
    }
    EventManager.resetCursor(this);
    this.activeIndex = null;
    this.drag = null;
    this.previewPoints = null;
    this.hoveredIndex = null;
    this.hoveredSide = null;
  }

  canDisplay() {
    const shape = this.shape;
    return shape.freeFormThickness > 0 && shape.getInflectionPoints()?.length > 2
      && !shape.isHidden && !shape.isDisabledInPopup && !SqftStore.isSlabMode()
      && !workspace.getState().loading;
  }

  canEdit() {
    const shape = this.shape;
    return this.canDisplay() && !shape.getState().isRotatable
      && !shape.shapePieces.length && !shape.bumpoutPieces.length
      && !shape.getLines().some(line => line.isWithBumpout() || line.isWithMitered()
        || line.from.angle || line.from.arcLength);
  }

  getGuide(points, index) {
    const pivot = getPointField(points[index].x, points[index].y, this.shape);
    const next = points[index + 1];
    const direction = Math.atan2(next.y - points[index].y, next.x - points[index].x);
    const turn = getFreeFormTurn(points, index);
    const start = direction - turn;
    const outline = this.getOutline(points)
      .map(point => getPointField(point.x, point.y, this.shape));
    const startLimit = Math.max(4, getFreeFormRayLimit(pivot, start, outline) - 6);
    const radius = Math.max(4, Math.min(48, this.shape.freeFormThickness * config.scale * 0.55,
      startLimit - 4));
    const startLength = Math.min(radius + 3, startLimit);
    const endLength = 72;
    const handle = {x: pivot.x + endLength * Math.cos(direction), y: pivot.y + endLength * Math.sin(direction)};
    const beforeDirection = start + Math.PI;
    const beforeHandle = {x: pivot.x + endLength * Math.cos(beforeDirection), y: pivot.y + endLength * Math.sin(beforeDirection)};
    let labelDistance = 24;
    let label = null;
    const labelDirections = [direction + Math.PI, start + turn / 2 + Math.PI / 2, start + turn / 2 - Math.PI / 2];
    while (!label) {
      for (const labelDirection of labelDirections) {
        const candidate = {x: pivot.x + labelDistance * Math.cos(labelDirection), y: pivot.y + labelDistance * Math.sin(labelDirection)};
        if (![handle, beforeHandle].some(marker => Math.abs(candidate.x - marker.x) < 48 && Math.abs(candidate.y - marker.y) < 30)) {
          label = candidate;
          break;
        }
      }
      labelDistance += 16;
    }
    return {pivot, radius, handle, beforeHandle, label, start, direction, beforeDirection, turn, startLength, endLength};
  }

  getHandleSide(mouse, guide) {
    const beforeDistance = Math.hypot(mouse.x - guide.beforeHandle.x, mouse.y - guide.beforeHandle.y);
    const afterDistance = Math.hypot(mouse.x - guide.handle.x, mouse.y - guide.handle.y);
    if (Math.min(beforeDistance, afterDistance) >= 16) {
      return null;
    }
    return beforeDistance < afterDistance ? 'before' : 'after';
  }

  isGuideHovered(mouse, guide) {
    const angle = Math.atan2(mouse.y - guide.pivot.y, mouse.x - guide.pivot.x);
    const delta = Math.atan2(Math.sin(angle - guide.start), Math.cos(angle - guide.start));
    const isOnArc = Math.abs(Math.hypot(mouse.x - guide.pivot.x, mouse.y - guide.pivot.y) - guide.radius) < 10
      && (guide.turn >= 0 ? delta >= 0 && delta <= guide.turn : delta <= 0 && delta >= guide.turn);
    return isOnArc || Boolean(this.getHandleSide(mouse, guide));
  }

  openAngleInput(index, guide) {
    this.close();
    this.activeIndex = index;
    const editPoints = this.shape.getInflectionPoints().map(point => ({x: point.x, y: point.y}));
    const turn = getFreeFormTurn(editPoints, index);
    const numberText = this.getAngleLabel(index, guide, Math.abs(turn) * 180 / Math.PI);
    numberText.callback = degrees => {
      if (!this.canEdit()) {
        return false;
      }
      const mapping = this.getPointMapping();
      const target = (turn < 0 ? -1 : 1) * degrees * Math.PI / 180;
      if (!mapping || !this.updatePreview(editPoints, index, target - turn)) {
        return false;
      }
      this.applyPreview(mapping);
      return true;
    };
    numberText.design.onInputClose = () => {
      this.angleInput = null;
      this.activeIndex = null;
      this.hoveredIndex = null;
    };
    numberText.state.isHovered = true;
    numberText.design.openInput = () => {
      const canvasBounds = EventManager.canvas.getBoundingClientRect();
      const container = EventManager.canvas.closest('.sqft');
      const bounds = container.getBoundingClientRect();
      const left = canvasBounds.left - bounds.left + guide.label.x;
      const top = canvasBounds.top - bounds.top + guide.label.y;
      this.angleInput = openFreeFormAnglePopup(`free-form-angle-${this.shape.id}`, numberText.value, {
        left: Math.max(8, Math.min(bounds.width - 240, left - 100)),
        top: Math.max(30, Math.min(bounds.height - 100, top + 20))
      }, numberText.callback, numberText.design.onInputClose);
    };
    numberText.onMouseDown(guide.label, {});
  }

  getAngleLabel(index, guide, degrees) {
    if (!this.angleLabels[index]) {
      this.angleLabels[index] = new NumberText(degrees, {x: 0, y: 0}, {
        isAngle: true, valuePostfix: '°', fontHeight: 13, shouldRenderFrameBorder: true
      }, this.shape, () => {}, false);
    }
    const label = this.angleLabels[index];
    const offset = this.shape.getOffset();
    label.setPos({x: (guide.label.x - offset.x) / config.scale, y: (guide.label.y - offset.y) / config.scale});
    label.setValue(Math.round(degrees * 10) / 10);
    return label;
  }

  getPointMapping() {
    const points = this.shape.getInflectionPoints();
    const mapping = matchFreeFormOutline(this.getOutline(points), this.shape.getPoints());
    if (mapping || !this.canEdit()) {
      return mapping;
    }
    const recovered = recoverFreeFormCenterline(this.shape.getPoints(), points);
    if (!recovered) {
      return null;
    }
    this.close();
    this.shape.setInflectionPoints(recovered.points);
    this.shape.freeFormSegmentWidths = recovered.widths;
    this.shape.freeFormThickness = recovered.widths[0];
    return recovered.mapping;
  }

  getOutline(points) {
    return getFreeFormAngleOutline(points, this.shape.freeFormSegmentWidths || this.shape.freeFormThickness);
  }

  onMouseDown = (mouse, event) => {
    if (!this.canEdit() || (event && event.button === 2)) {
      this.close();
      return;
    }
    const mapping = this.getPointMapping();
    if (!mapping) {
      return;
    }
    const points = this.shape.getInflectionPoints();
    for (let index = 1; index < points.length - 1; index++) {
      const guide = this.getGuide(points, index);
      const isLabel = Math.abs(mouse.x - guide.label.x) < 30 && Math.abs(mouse.y - guide.label.y) < 14;
      const isHandle = this.isGuideHovered(mouse, guide);
      if (!isLabel && !isHandle) {
        continue;
      }
      if (isLabel && !isHandle) {
        this.openAngleInput(index, guide);
        return true;
      }
      this.close();
      this.activeIndex = index;
      this.drag = {
        side: this.getHandleSide(mouse, guide) || 'after',
        points: points.map(point => ({x: point.x, y: point.y})),
        mapping,
        lastPointerAngle: Math.atan2(mouse.y - guide.pivot.y, mouse.x - guide.pivot.x),
        rotation: 0
      };
      return true;
    }
    this.close();
  }

  onMouseMove = (mouse, event) => {
    if (!this.drag) {
      this.hoveredIndex = null;
      this.hoveredSide = null;
      if (this.canEdit() && !event?.terminate) {
        const points = this.shape.getInflectionPoints();
        for (let index = 1; index < points.length - 1; index++) {
          const guide = this.getGuide(points, index);
          const {label} = guide;
          if ((Math.abs(mouse.x - label.x) < 30 && Math.abs(mouse.y - label.y) < 14)
            || this.isGuideHovered(mouse, guide)) {
            if (this.getPointMapping()) {
              EventManager.setCursor('pointer', this);
              this.hoveredIndex = index;
              this.hoveredSide = this.getHandleSide(mouse, guide);
              return true;
            }
          }
        }
      }
      EventManager.resetCursor(this);
      return;
    }
    if (!this.canEdit()) {
      this.close();
      return;
    }
    const index = this.activeIndex;
    const guide = this.getGuide(this.drag.points, index);
    const angle = Math.atan2(mouse.y - guide.pivot.y, mouse.x - guide.pivot.x);
    // Accumulate small pointer steps so a single drag can cross the atan2 seam.
    const pointerStep = Math.atan2(Math.sin(angle - this.drag.lastPointerAngle), Math.cos(angle - this.drag.lastPointerAngle));
    this.drag.lastPointerAngle = angle;
    this.drag.rotation += pointerStep;
    const originalTurn = getFreeFormTurn(this.drag.points, index);
    const sideSign = this.drag.side === 'before' ? -1 : 1;
    const requestedTurn = originalTurn + this.drag.rotation * sideSign;
    let turn = requestedTurn;
    if (!event?.shiftKey) {
      turn = Math.round(turn / (Math.PI / 180)) * Math.PI / 180;
    }
    // Avoid folding the two sides back onto one another.
    turn = Math.max(-Math.PI * 0.95, Math.min(Math.PI * 0.95, turn));
    if (Math.abs(requestedTurn) > Math.PI * 0.95) {
      this.drag.rotation = (turn - originalTurn) * sideSign;
    }
    const delta = (turn - originalTurn) * sideSign;
    this.updatePreview(this.drag.points, index, delta, this.drag.side);
    return true;
  }

  updatePreview(points, index, delta, side = 'after') {
    const rotated = rotateFreeFormContinuation(points, index, delta, side);
    const outline = this.getOutline(rotated);
    if (outline.length === this.shape.getPoints().length
      && outline.every(point => Number.isFinite(point.x) && Number.isFinite(point.y))
      && isSimpleFreeFormOutline(outline)) {
      this.previewPoints = rotated;
      return true;
    }
    return false;
  }

  onMouseLeave = () => {
    if (!this.angleInput) {
      this.close();
    }
  }

  // A long press during rotation must not finish an unrelated drawing tool.
  onContextMenu = () => {
    return Boolean(this.drag);
  }

  onMouseUp = () => {
    if (!this.drag) {
      return;
    }
    this.applyPreview(this.drag.mapping);
    this.drag = null;
    this.previewPoints = null;
    this.activeIndex = null;
    this.hoveredIndex = null;
    return true;
  }

  applyPreview(mapping) {
    if (this.previewPoints && this.canEdit()) {
      const outline = this.getOutline(this.previewPoints);
      this.shape.getPoints().forEach((point, index) => {
        const position = outline[mapping[index]];
        point.x = position.x;
        point.y = position.y;
      });
      this.shape.setInflectionPoints(this.previewPoints);
      this.shape.isPointsChanged = true;
      const {updateCalculations} = store.getState().sqftState;
      store.dispatch(updateSQFTCalculations(updateCalculations + 1));
    }
    this.previewPoints = null;
  }

  onKeyDown = (code) => {
    if (code === "Escape" && this.activeIndex !== null) {
      this.close();
      return true;
    }
  }

  render(ctx) {
    if (!this.canDisplay() || !this.getPointMapping()) {
      this.close();
      return;
    }
    const points = this.previewPoints || this.shape.getInflectionPoints();
    const theme = COLOR_THEMES[config.activeTheme] || COLOR_THEMES.qc;
    const dimensionColor = theme.primary;
    const activeColor = theme.active;
    ctx.save();
    ctx.strokeStyle = activeColor;
    ctx.fillStyle = activeColor;
    ctx.lineWidth = 1.5;
    if (this.previewPoints) {
      const outline = this.getOutline(points);
      ctx.beginPath();
      outline.forEach((point, index) => {
        const position = getPointField(point.x, point.y, this.shape);
        if (index === 0) {
          ctx.moveTo(position.x, position.y);
        } else {
          ctx.lineTo(position.x, position.y);
        }
      });
      ctx.closePath();
      ctx.stroke();
    }
    for (let index = 1; index < points.length - 1; index++) {
      const {pivot, radius, handle, beforeHandle, label, start, direction, turn, startLength} = this.getGuide(points, index);
      const isActive = this.activeIndex === index || this.hoveredIndex === index;
      const selectedSide = this.drag?.side || this.hoveredSide;
      ctx.strokeStyle = dimensionColor;
      ctx.lineWidth = isActive ? 2 : 1.5;
      ctx.beginPath();
      ctx.moveTo(pivot.x, pivot.y);
      ctx.arc(pivot.x, pivot.y, radius, start, direction, turn < 0);
      ctx.closePath();
      ctx.save();
      ctx.fillStyle = dimensionColor;
      ctx.globalAlpha = isActive ? 0.12 : 0.06;
      ctx.fill();
      ctx.restore();
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(pivot.x + startLength * Math.cos(start), pivot.y + startLength * Math.sin(start));
      ctx.lineTo(pivot.x, pivot.y);
      ctx.lineTo(pivot.x + (radius + 3) * Math.cos(direction), pivot.y + (radius + 3) * Math.sin(direction));
      ctx.stroke();
      ctx.setLineDash([]);
      for (const lever of [{position: beforeHandle, side: 'before'}, {position: handle, side: 'after'}]) {
        ctx.save();
        ctx.globalAlpha = isActive && selectedSide === lever.side ? 0.9 : 0.25;
        ctx.beginPath();
        ctx.moveTo(pivot.x, pivot.y);
        ctx.lineTo(lever.position.x, lever.position.y);
        ctx.stroke();
        ctx.restore();
      }
      ctx.beginPath();
      ctx.arc(pivot.x, pivot.y, radius, start, direction, turn < 0);
      ctx.stroke();
      if (isActive && selectedSide) {
        ctx.save();
        ctx.strokeStyle = activeColor;
        ctx.globalAlpha = 0.22;
        ctx.lineWidth = Math.max(8, this.shape.freeFormThickness * config.scale);
        ctx.lineJoin = 'round';
        ctx.beginPath();
        const firstPointIndex = selectedSide === 'before' ? 0 : index;
        const lastPointIndex = selectedSide === 'before' ? index : points.length - 1;
        for (let pointIndex = firstPointIndex; pointIndex <= lastPointIndex; pointIndex++) {
          const position = getPointField(points[pointIndex].x, points[pointIndex].y, this.shape);
          if (pointIndex === firstPointIndex) {
            ctx.moveTo(position.x, position.y);
          } else {
            ctx.lineTo(position.x, position.y);
          }
        }
        ctx.stroke();
        ctx.restore();
        ctx.beginPath();
        ctx.arc(pivot.x, pivot.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = dimensionColor;
        ctx.fill();
      }
      for (const marker of [{position: beforeHandle, side: 'before'}, {position: handle, side: 'after'}]) {
        ctx.save();
        ctx.globalAlpha = isActive && selectedSide === marker.side ? 1 : 0.3;
        ctx.beginPath();
        ctx.arc(marker.position.x, marker.position.y, 9, 0, Math.PI * 2);
        ctx.fillStyle = '#fff';
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = dimensionColor;
        ctx.font = '15px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('↻', marker.position.x, marker.position.y);
        ctx.restore();
      }
      const degrees = Math.abs(getFreeFormTurn(points, index)) * 180 / Math.PI;
      this.getAngleLabel(index, {label}, degrees).render(ctx);
    }
    ctx.restore();
  }
}
